import { Box, Heading, Stack, Text, useColorMode } from '@chakra-ui/react';
import { translationAction } from 'actions';
import { setOutputLanguage } from 'actions/translation';
import { ActionMeta, MultiValue, Select } from 'chakra-react-select';
import { TRANSLATED_FILE_STATUS } from 'helpers/enum';
import { RootState } from 'helpers/store';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

type Option = { label: string; value: string };

const LanguagesMode = () => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const dispatch = useDispatch();
  const { data: languages } = useSelector((store: RootState) => store.language);
  const { current } = useSelector((store: RootState) => store.translation);
  const isTranslating = useSelector(
    (state) =>
      state.translation.current.results?.some(
        (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
      )
  );

  const outputLanguageSelections = current?.output_languages.map((lang) => {
    return {
      label: lang.name,
      value: lang.code,
    };
  });

  const languageOptions = useMemo(() => {
    return languages?.map(({ code, name }) => ({ label: name, value: code }));
  }, [languages]);

  const translateFromOptions = [
    { label: t('translator.sourceLanguageLabel'), value: '' },
    ...languageOptions,
  ];

  const setInputLanguage = (option: Option | null, _actionMeta: ActionMeta<Option>) => {
    if (option) {
      dispatch(translationAction.setInputLanguage([{ code: option.value, name: option.label }]));
    }
  };

  const handleSelectChange = (
    e: MultiValue<{
      label: string;
      value: string;
    }>
  ) => {
    // setOutputLanguageSelections([...e]);
    const outputLanguages = e.map((lang: { label: string; value: string }) => {
      return {
        code: lang.value,
        name: lang.label,
      };
    });

    dispatch(setOutputLanguage(outputLanguages));
  };

  useEffect(() => {
    dispatch(translationAction.fetchAllLanguageData());
  }, []);

  return (
    <Box
      h={'100%'}
      maxH='calc(100vh - 178px)'
      borderRight={isDark ? '1px' : ''}
      borderRadius={!isDark ? '20px' : ''}
      shadow='shadow_box'
    >
      <Stack direction={'column'} h='100%' minH='100%' px={5}>
        <Box>
          <Heading as='h3' my={6} mr='1rem' color={'white.600'} fontWeight={'bold'} size='md'>
            {t('translator.languages')}
          </Heading>
          <Text mt='2' fontSize={'sm'}>
            {t('translator.languageInstructions')}
          </Text>
        </Box>

        <Box mt={2}>
          <Stack gap={2}>
            <Heading fontSize={'sm'} fontWeight={'bold'}>
              {t('translator.translateFrom')}
            </Heading>
          </Stack>
          <Box mt={1}>
            <Select
              value={
                translateFromOptions?.find(
                  (e) => current?.input_languages?.[0]?.code === e.value
                ) ?? translateFromOptions[0]
              }
              name='translation-input'
              options={translateFromOptions}
              onChange={setInputLanguage}
              placeholder={`${translateFromOptions[0].label}`}
              isDisabled={isTranslating}
            ></Select>
          </Box>
        </Box>

        <Box mt={6}>
          <Stack gap={2}>
            <Heading fontSize={'sm'} fontWeight={'bold'}>
              {t('translator.targetLanguageLabel')}
            </Heading>
            <Box fontSize={'x-small'}>
              <Select
                menuPlacement='auto'
                placeholder={t('translator.targetLanguagePlaceholder')}
                options={languageOptions}
                isMulti
                onChange={handleSelectChange}
                value={outputLanguageSelections}
                isOptionDisabled={(_option: Option) => outputLanguageSelections.length >= 5}
                isDisabled={isTranslating}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

export default LanguagesMode;
